import type { XrefSeals } from "@/composables/forms/formTypes";
import { LengthUnits } from "@/libs/units/length";
import { MassUnits } from "@/libs/units/mass";
import { PercentageUnits } from "@/libs/units/percentage";
import { PowerUnits } from "@/libs/units/power";
import { VolumeUnits } from "@/libs/units/volume";
import { arrayOfInts } from "@/utils/utils";
import type { RenderScenarioExportCols } from "./sizingExportTypes";

export const renderSealCols: RenderScenarioExportCols = ({
  calcs,
  targetUnits,
  maxSeals
}) =>
  arrayOfInts(maxSeals).flatMap(i => {
    let sealNum = String(i + 1);
    const rowName = calcs.isScenarioEspShore.value ? "Seal" : "Thrust Chamber";
    if (sealNum === "1") sealNum = "UT";
    else if (sealNum === "2") sealNum = "LT";
    const section = calcs.isScenarioEspShore.value
      ? `SEAL ${sealNum}`
      : `THRUST CHAMBER`;
    const seal: XrefSeals[number] | undefined =
      calcs.descendingOrderSeals.value[i];
    return [
      {
        section,
        label: `${rowName} OD`,
        targetUnit: LengthUnits.Inches,
        value: seal?.r_seal?.outer_diameter_inches,
        disableConversion: true
      },
      {
        section,
        label: `${rowName} Length`,
        targetUnit: targetUnits.largeLengthUnit,
        value: LengthUnits.Inches.toStandardUnit(seal?.r_seal?.length_inches)
      },
      {
        section,
        label: `${rowName} Weight`,
        targetUnit: targetUnits.massUnit,
        value: seal?.r_seal?.weight_pounds
      },
      {
        section,
        label: `${rowName} Configuration`,
        targetUnit: null,
        value: seal?.r_seal?.configuration
      },
      {
        section,
        label: `${rowName} Bearing Rating @ 3500 RPM`,
        targetUnit: MassUnits.Pounds,
        value: seal?.r_seal?.standard_bearing_rating_pounds
      },
      {
        section,
        label: `${rowName} Bearing Load Ratio`,
        targetUnit: PercentageUnits.Percentage,
        value: PercentageUnits.Ratio.toStandardUnit(
          calcs.sealCalculations.individual.value[seal?.id]?.bearingLoadRatio
        )
      },
      {
        section,
        label: `${rowName} Shut-in Bearing Ratio`,
        targetUnit: PercentageUnits.Percentage,
        value: PercentageUnits.Ratio.toStandardUnit(
          calcs.sealCalculations.individual.value[seal?.id]
            ?.shutInBearingLoadRatio
        )
      },
      {
        section,
        label: `${rowName} Std Shaft Rating @ 3500 RPM`,
        targetUnit: PowerUnits.HorsePower,
        value: seal?.r_seal?.standard_shaft_limit_hp
      },
      {
        section,
        label: `${rowName} High Strength Shaft Rating @ 3500 RPM`,
        targetUnit: PowerUnits.HorsePower,
        value: seal?.r_seal?.high_strength_shaft_limit_hp
      },
      {
        section,
        label: `% Standard Shaft ${rowName} Load`,
        targetUnit: PercentageUnits.Percentage,
        value: PercentageUnits.Ratio.toStandardUnit(
          calcs.sealCalculations.individual.value[seal?.id]
            ?.standardShaftRatingRatio
        )
      },
      {
        section,
        label: `% High Strength Shaft ${rowName} Load`,
        targetUnit: PercentageUnits.Percentage,
        value: PercentageUnits.Ratio.toStandardUnit(
          calcs.sealCalculations.individual.value[seal?.id]
            ?.highStrengthShaftRatingRatio
        )
      },
      {
        section,
        label: `${rowName} HP Required`,
        targetUnit: targetUnits.hydraulicPowerUnit,
        value: calcs.sealCalculations.individual.value[seal?.id]?.operatingHP
      },
      {
        section,
        label: `${rowName} Expansion Capacity`,
        targetUnit: targetUnits.volumeUnit,
        value: VolumeUnits.Gallons.toStandardUnit(
          calcs.sealCalculations.individual.value[seal?.id]
            ?.operationalTotalExpansionGal
        ),
        hidden: !calcs.isScenarioEspShore.value
      },
      {
        section,
        label: `Oil Expansion`,
        targetUnit: targetUnits.volumeUnit,
        value: VolumeUnits.Gallons.toStandardUnit(
          calcs.sealCalculations.individual.value[seal?.id]
            ?.operationalTotalAmountOfExpansionGal
        ),
        hidden: !calcs.isScenarioEspShore.value
      },
      {
        section,
        label: `${rowName} Expansion Capacity Ratio`,
        targetUnit: null,
        value:
          calcs.sealCalculations.individual.value[seal?.id]
            ?.operationExpansionRatio,
        hidden: !calcs.isScenarioEspShore.value
      }
    ];
  });
